import React, { useState, useEffect } from 'react';
import { iconBtn, iconBtnDanger } from '../../components/ui/iconButton';
import { motion } from 'framer-motion';
import Table from '../../components/Table';
import Button from '../../components/Button';
import PageHeader from '../../components/PageHeader';
import { FaUserPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaToggleOn, FaToggleOff, FaLink } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/toast';
import { swalConfirm, swalInput, swalSuccess, swalError } from '../../utils/swal';
import { useNavigate } from 'react-router-dom';
//services
import { clientService } from '../../services/clientService';
//supabase (para invocar edge function)
import { supabase } from '../../config/supabaseConfig.ts';
// === LEGACY BCRYPT — ELIMINAR DESPUÉS DE MIGRACIÓN ===
import bcrypt from 'bcryptjs';
// =====================================================
//utils
import { formatdateWithTime, formatPhoneNumber } from '../../utils/helpers';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const users = await clientService.getAll();
      console.log(users);
      setUsers(users);
    } catch (error) {
      console.error('Error al obtener usuarios:', error.message);
    }
  };

  // Vincular cliente existente con auth user
  const handleVincular = async (clienteRow) => {
    const result = await swalInput(
      `Vincular a ${clienteRow.full_name || clienteRow.email}`,
      'Ingresa una contraseña para este cliente',
      'password',
      {
        inputAttributes: { minlength: 6 },
        inputValidator: (value) => {
          if (!value || value.length < 6) {
            return 'La contraseña debe tener al menos 6 caracteres';
          }
        },
      }
    );

    if (!result.isConfirmed || !result.value) return;

    try {
      const { data: authData, error: authError } = await supabase.functions.invoke('create-client-user', {
        body: {
          email: clienteRow.email,
          password: result.value,
          full_name: clienteRow.full_name || clienteRow.email,
        },
      });

      // supabase.functions.invoke puede devolver el error en data cuando la función retorna non-2xx
      const funcError = authError || (authData?.error ? new Error(authData.error) : null);

      if (funcError || !authData?.id) {
        const msg = authData?.error || funcError?.message || 'Error al crear usuario de autenticación';
        toastError(msg);
        console.error('[handleVincular] edge function error:', funcError, authData);
        return;
      }

      // === LEGACY BCRYPT — ELIMINAR DESPUÉS DE MIGRACIÓN ===
      const legacyHash = bcrypt.hashSync(result.value, 12);
      await supabase.from('clientes_usuario').update({ password: legacyHash }).eq('id', clienteRow.id);
      // =====================================================

      const { error: linkError } = await clientService.linkAuthUser(clienteRow.id, authData.id);
      if (linkError) {
        toastError(linkError.message || 'Error al vincular el cliente');
        return;
      }

      fetchUsers();
      toastSuccess('Cliente vinculado correctamente');
    } catch (error) {
      console.error('[handleVincular] error:', error);
      toastError(error.message || 'Error al vincular el cliente');
    }
  };

  // Definición de las columnas de la tabla
  const columns = [
    {
      key: 'full_name',
      title: 'Nombre',
      sortable: true,
      render: (value, row) => (
        <span className="flex items-center gap-2">
          {value}
          {!row.auth_user_id && (
            <span className="inline-flex items-center h-6 px-2.5 rounded-full border border-caution/25 bg-caution-soft text-caution text-[12px] font-semibold whitespace-nowrap">
              Sin vincular
            </span>
          )}
        </span>
      )
    },
    {
      key: 'email',
      title: 'Correo',
      sortable: true,
    },
    {
      key: 'whatsapp_number',
      title: 'Whatsapp',
      sortable: true,
      render: (value) => value ? value : 'Sin número'
    },
    {
      key: 'proyectos',
      title: 'Proyectos',
      sortable: true,
      render: (value) => (
        <span>{
          value.length > 0 ?
          value.map(proyecto => proyecto.proyecto.name).join(', ') :
          'Sin proyectos asignados'
        }</span>
      )
    },
    {
      key: 'status',
      title: 'Estado',
      sortable: true,
      render: (value) => (
        value === 'active' ?
          <span className="flex items-center gap-1 text-live font-semibold"><FaCheck className="mr-1" /> Activo</span> :
          <span className="flex items-center gap-1 text-signal font-semibold"><FaTimes className="mr-1" /> Inactivo</span>
      )
    },
    {
      key: 'last_access_time',
      title: 'Último acceso',
      sortable: true,
      render: (value) => value ? formatdateWithTime(value) : 'Sin registro'
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      render: (_, row) => (
        <div className="flex space-x-2">
          <button
            disabled={row.status === 'inactive'}
            onClick={() => handleEdit(row)}
            className={iconBtn}
            title="Editar"
          >
            <FaEdit />
          </button>
          <button
            disabled={row.status === 'inactive'}
            onClick={() => handleDelete(row.id)}
            className={iconBtnDanger}
            title="Eliminar"
          >
            <FaTrash />
          </button>
          <button
            onClick={() => handleStatusChange(row.id)}
            className={iconBtn}
            title="Cambiar estado"
          >
            {row.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
          </button>
          {!row.auth_user_id && (
            <button
              onClick={() => handleVincular(row)}
              className={iconBtn}
              title="Vincular con autenticación"
            >
              <FaLink />
            </button>
          )}
        </div>
      )
    },
  ];

  // Opciones de la tabla
  const tableOptions = {
    pagination: true,
    pageSize: 5,
    pageSizeOptions: [5, 10, 20],
    showSearch: true,
    searchPlaceholder: 'Buscar usuario...',
    emptyMessage: 'No hay usuarios disponibles',
  };

  const handleAddUser = async () => {
    navigate('/clients/add', { state: { isEdit: false, userEdit: null } });
  };

  const handleEdit = async (user) => {
    navigate('/clients/edit', { state: { isEdit: true, userEdit: user } });
  };

  const handleDelete = async (userId) => {
    const { isConfirmed } = await swalConfirm(
      '¿Eliminar usuario?',
      '¿Está seguro de que desea eliminar este usuario? Esta acción no se puede deshacer.',
      'Sí, eliminar',
      {
        input: null,
        html: null,
        footer: null,
        showDenyButton: false,
        denyButtonText: undefined
      }
    );

    if (isConfirmed) {
      await clientService.deleteClient(userId);
      fetchUsers();
      toastSuccess('Usuario eliminado correctamente');
    }
  };

  const handleStatusChange = async (userId) => {
    const { isConfirmed } = await swalConfirm(
      '¿Cambiar estado?',
      '¿Está seguro de que desea cambiar el estado de este usuario? Esta acción no se puede deshacer.',
      'Sí, cambiar',
      {
        input: null,
        html: null,
        footer: null,
        showDenyButton: false,
        denyButtonText: undefined
      }
    );

    if (isConfirmed) {
      const status = users.find(user => user.id === userId).status == "active" ? "inactive" : "active";
      await clientService.toggleClienteStatus(userId, status);
      fetchUsers();
      toastSuccess('Estado del cliente cambiado correctamente');
    }
  };

  return (
    <div>
      <PageHeader title="Clientes" subtitle="Gestiona usuarios y sus proyectos asignados">
        <Button variant="primary" size="lg" onClick={handleAddUser} leftIcon={<FaUserPlus size={12} />}>
          Agregar Usuario
        </Button>
      </PageHeader>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16,1,0.3,1] }}
      >

        <Table data={users} columns={columns} options={tableOptions} />
      </motion.div>
    </div>
  );
};

export default UsersPage;
